import { useNavigate } from "react-router-dom";
import { Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const { items, setQuantity, remove, subtotal, isOpen, setIsOpen } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FDFBF7] border-l border-[#1A6B2E]/10 p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-6 border-b border-[#1A6B2E]/10">
          <SheetTitle className="font-display text-2xl tracking-tighter text-left">
            Your Chop Box
          </SheetTitle>
          <p className="text-xs font-label text-[#4E5C4F]">
            {items.length} {items.length === 1 ? "item" : "items"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto chop-scroll p-6 space-y-4">
          {items.length === 0 ? (
            <div className="h-full grid place-items-center text-center py-16">
              <div>
                <ShoppingBag className="w-10 h-10 text-[#D02030] mx-auto mb-3" />
                <p className="text-[#4E5C4F]">Your box is empty. Time to eat!</p>
              </div>
            </div>
          ) : (
            items.map((it) => (
              <div key={it.cartKey} data-testid={`cart-item-${it.cartKey}`} className="flex gap-4">
                <img
                  src={it.image}
                  alt={it.name}
                  className="w-20 h-20 rounded-xl object-cover shrink-0 border border-[#1A6B2E]/10"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-display text-base leading-tight">{it.name}</h4>
                      {it.variant_name && (
                        <p className="text-[11px] font-label text-[#1A6B2E] mt-0.5">{it.variant_name}</p>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${it.cartKey}`}
                      onClick={() => remove(it.cartKey)}
                      className="text-[#4E5C4F] hover:text-[#D02030] p-1"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-[#4E5C4F] mt-0.5">${it.price.toFixed(2)} each</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1 rounded-full border border-[#1A6B2E]/20 p-1">
                      <button
                        data-testid={`cart-minus-${it.cartKey}`}
                        onClick={() => setQuantity(it.cartKey, it.quantity - 1)}
                        className="w-7 h-7 grid place-items-center rounded-full hover:bg-[#F4EFEB]"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center text-sm font-semibold tabular-nums">
                        {it.quantity}
                      </span>
                      <button
                        data-testid={`cart-plus-${it.cartKey}`}
                        onClick={() => setQuantity(it.cartKey, it.quantity + 1)}
                        className="w-7 h-7 grid place-items-center rounded-full hover:bg-[#F4EFEB]"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="font-display text-base">
                      ${(it.price * it.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-[#1A6B2E]/10 space-y-4 bg-[#F4EFEB]/40">
            <div className="flex items-center justify-between">
              <span className="font-label text-xs text-[#4E5C4F]">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <button
              data-testid="cart-checkout-btn"
              onClick={goToCheckout}
              className="btn-primary w-full rounded-full py-3.5 text-sm font-semibold"
            >
              Book delivery & pay
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
